/**
 * Browser microphone recorder (limited web support for the AI note taker).
 *
 * Captures mic-only audio via getUserMedia + MediaRecorder — browsers cannot
 * capture system audio, so remote participants are not recorded; the desktop
 * app provides the full experience. Output is webm/opus (or mp4/aac on
 * Safari), both handled by the server transcription pipeline.
 */

export interface WebRecorderResult {
  blob: Blob
  mimeType: string
  durationMs: number
}

interface WebRecorderCallbacks {
  /** ~5Hz RMS mic level, 0.0–1.0 */
  onLevel?: (level: number) => void
  /** 1s duration ticks (excludes paused time) */
  onDuration?: (durationMs: number) => void
}

type BrowserAudioContext = typeof AudioContext

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return ''
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
  return candidates.find(type => MediaRecorder.isTypeSupported(type)) ?? ''
}

export function isWebRecordingSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  )
}

export function fileExtensionForMimeType(mimeType: string): string {
  if (mimeType.includes('mp4')) return 'm4a'
  if (mimeType.includes('ogg')) return 'ogg'
  return 'webm'
}

export class WebMicRecorder {
  private stream: MediaStream | null = null
  private recorder: MediaRecorder | null = null
  private chunks: BlobPart[] = []
  private mimeType = ''

  private audioContext: AudioContext | null = null
  private analyser: AnalyserNode | null = null
  private levelTimer: ReturnType<typeof setInterval> | null = null
  private durationTimer: ReturnType<typeof setInterval> | null = null

  private recordedMs = 0
  private segmentStartedAt: number | null = null

  constructor(private readonly callbacks: WebRecorderCallbacks = {}) {}

  get durationMs(): number {
    const live = this.segmentStartedAt
      ? performance.now() - this.segmentStartedAt
      : 0
    return Math.round(this.recordedMs + live)
  }

  /** Requests mic access (browser shows its native permission prompt). */
  async start(): Promise<void> {
    if (this.recorder) throw new Error('Recording already in progress')

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      })

      this.mimeType = pickMimeType()
      this.recorder = new MediaRecorder(
        this.stream,
        this.mimeType
          ? { mimeType: this.mimeType, audioBitsPerSecond: 64_000 }
          : undefined,
      )
      this.chunks = []
      this.recorder.ondataavailable = event => {
        if (event.data.size > 0) this.chunks.push(event.data)
      }
      // 1s timeslice keeps chunks flowing so a tab crash loses little
      this.recorder.start(1000)
      this.segmentStartedAt = performance.now()

      this.setupLevelMeter()
      this.durationTimer = setInterval(() => {
        try {
          this.callbacks.onDuration?.(this.durationMs)
        } catch {
          // UI duration updates are cosmetic; don't let callback failures
          // destabilize the recording.
        }
      }, 1000)
    } catch (error) {
      this.cleanup()
      throw error
    }
  }

  pause(): void {
    if (!this.recorder || this.recorder.state !== 'recording') return
    this.recorder.pause()
    if (this.segmentStartedAt !== null) {
      this.recordedMs += performance.now() - this.segmentStartedAt
      this.segmentStartedAt = null
    }
  }

  resume(): void {
    if (!this.recorder || this.recorder.state !== 'paused') return
    this.recorder.resume()
    this.segmentStartedAt = performance.now()
  }

  get isPaused(): boolean {
    return this.recorder?.state === 'paused'
  }

  async stop(): Promise<WebRecorderResult> {
    const recorder = this.recorder
    if (!recorder) throw new Error('No recording in progress')

    if (this.segmentStartedAt !== null) {
      this.recordedMs += performance.now() - this.segmentStartedAt
      this.segmentStartedAt = null
    }

    const stopped = new Promise<void>(resolve => {
      recorder.onstop = () => resolve()
    })
    recorder.stop()
    await stopped

    const mimeType = this.mimeType || recorder.mimeType || 'audio/webm'
    const blob = new Blob(this.chunks, { type: mimeType })
    const durationMs = Math.round(this.recordedMs)
    this.cleanup()
    return { blob, mimeType, durationMs }
  }

  /** Discard everything without producing a result. */
  cancel(): void {
    try {
      if (this.recorder && this.recorder.state !== 'inactive') {
        this.recorder.stop()
      }
    } catch {
      // already stopped
    }
    this.cleanup()
  }

  private setupLevelMeter(): void {
    if (!this.stream || !this.callbacks.onLevel) return
    try {
      const AudioContextCtor =
        globalThis.AudioContext ??
        (
          globalThis as typeof globalThis & {
            webkitAudioContext?: BrowserAudioContext
          }
        ).webkitAudioContext
      if (!AudioContextCtor) return

      this.audioContext = new AudioContextCtor()
      const source = this.audioContext.createMediaStreamSource(this.stream)
      this.analyser = this.audioContext.createAnalyser()
      this.analyser.fftSize = 512
      source.connect(this.analyser)

      const data = new Float32Array(this.analyser.fftSize)
      this.levelTimer = setInterval(() => {
        try {
          if (!this.analyser) return
          if (this.recorder?.state === 'paused') {
            this.callbacks.onLevel?.(0)
            return
          }
          this.analyser.getFloatTimeDomainData(data)
          let sumSq = 0
          for (const sample of data) sumSq += sample * sample
          this.callbacks.onLevel?.(Math.min(1, Math.sqrt(sumSq / data.length)))
        } catch {
          if (this.levelTimer) clearInterval(this.levelTimer)
          this.levelTimer = null
        }
      }, 200)
    } catch {
      // Level meter is cosmetic — recording continues without it
    }
  }

  private cleanup(): void {
    if (this.levelTimer) clearInterval(this.levelTimer)
    if (this.durationTimer) clearInterval(this.durationTimer)
    this.levelTimer = null
    this.durationTimer = null
    this.analyser = null
    void this.audioContext?.close().catch(() => {})
    this.audioContext = null
    this.stream?.getTracks().forEach(track => track.stop())
    this.stream = null
    this.recorder = null
    this.chunks = []
    this.recordedMs = 0
    this.segmentStartedAt = null
  }
}
